import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

// Uses MUI's Typography rather than stylovyze's Text on purpose: this component is rendered from
// both tabs, and stylovyze ships a UMD bundle that Vitest cannot load without a mock. Depending on
// it here would force every consumer's spec to mock stylovyze whether it uses it or not.
import { DarkInfoTooltip } from '@Components/deteriorationModel/configurationTab/DarkInfoTooltip';
import { useGlobalization } from '@Translations';
import { WeibullFallbackBanner } from './WeibullFallbackBanner';
import { characteristicAge, describeCoefficients, WeibullParams } from './weibullFit';

interface WeibullFitNotesProps {
	// True when the charted curve never changes, whatever the API said about the fit succeeding.
	isFlat: boolean;
	params?: WeibullParams;
	// Names the group the notes describe. Used on the results tab, where one chart can show
	// several groups at once; omitted where the surrounding context already identifies the group.
	label?: string;
}

/**
 * Explains a fitted curve: whether it is usable, and which coefficients produced it.
 *
 * Shared by the configuration-tab preview and the results tab so the wording and its translations
 * have a single home — the two surfaces read the same fit from different endpoints, and a user who
 * saw one message in the preview should not meet different words for it in the results.
 */
export const WeibullFitNotes = ({ isFlat, params, label }: WeibullFitNotesProps): JSX.Element | null => {
	const { t } = useGlobalization();

	const coefficients = describeCoefficients(params);
	const age = characteristicAge(params);

	if (!isFlat && coefficients === null) return null;

	return (
		<Stack spacing={1} sx={{ mt: 1 }} data-testid="weibull-fit-notes">
			{isFlat && (
				<WeibullFallbackBanner
					testId="weibull-degenerate-fit-banner"
					// Deliberately does not name survival or failure probability: the preview charts one
					// and the results tab the other, and a single wording keeps the two surfaces
					// consistent for a user moving between them.
					message={t(
						'This curve is flat — the fitted coefficients produce no change over time, so remaining life cannot be estimated from it.',
					)}
				/>
			)}
			{coefficients !== null && (
				<Stack direction="row" spacing={0.5} alignItems="center" data-testid="weibull-fitted-coefficients">
					<Typography sx={{ fontSize: '0.8125rem', color: '#666' }}>
						{label
							? `${label} — ${t('Fitted coefficients')}: ${coefficients}`
							: `${t('Fitted coefficients')}: ${coefficients}`}
					</Typography>
					{/* Only meaningful for a 3-parameter fit: without a location, the characteristic age
					    is eta itself and the explanation would be noise. */}
					{age !== null && (
						<DarkInfoTooltip
							heading={t('Characteristic age')}
							body={[
								`c + η = ${age.toFixed(2)} ${t('years')}`,
								t(
									'The location parameter c absorbs most of the characteristic age, so η alone is smaller than the η of a 2-parameter fit for the same data. c + η is the quantity comparable to it.',
								),
							]}>
							<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
								<InfoOutlined sx={{ fontSize: 14, color: '#666' }} />
							</Box>
						</DarkInfoTooltip>
					)}
				</Stack>
			)}
		</Stack>
	);
};
